import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Share2, Download, Copy, Check, Image, Trophy, Flame, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CARD_ICONS = {
  weekly_stats: Flame,
  badge_earned: Trophy,
  streak_progress: Flame,
  vertical_record: TrendingUp,
};

const CARD_COLORS = {
  weekly_stats: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  badge_earned: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
  streak_progress: "text-red-400 bg-red-500/10 border-red-500/30",
  vertical_record: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
};

const SocialShare = () => {
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [generatedSvg, setGeneratedSvg] = useState(null);
  const [shareText, setShareText] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchAvailableCards();
  }, []);

  const fetchAvailableCards = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/share/cards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCards(response.data.cards || []);
    } catch (err) {
      toast.error("Failed to load share cards");
    }
  };

  const generateCard = async (cardType) => {
    setLoading(true);
    setSelectedCard(cardType);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/share/generate`,
        { card_type: cardType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedSvg(response.data.svg);
      setShareText(response.data.share_text || "");
      toast.success("Share card generated!");
    } catch (err) {
      toast.error("Failed to generate card");
    } finally {
      setLoading(false);
    }
  };

  const downloadSvg = () => {
    if (!generatedSvg) return;
    const blob = new Blob([generatedSvg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pivot-${selectedCard}-${Date.now()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Image downloaded!");
  };

  const copyShareText = () => {
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    toast.success("Share text copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Pivot Performance",
          text: shareText,
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      copyShareText();
    }
  };

  return (
    <div className="space-y-6" data-testid="social-share">
      {/* Card Selector */}
      <div className="grid grid-cols-2 gap-3">
        {cards.map((card) => {
          const Icon = CARD_ICONS[card.type] || Image;
          const colorClass = CARD_COLORS[card.type] || "text-zinc-400 bg-zinc-500/10 border-zinc-500/30";
          return (
            <button
              key={card.type}
              data-testid={`share-card-${card.type}`}
              onClick={() => generateCard(card.type)}
              disabled={!card.available || loading}
              className={`p-4 rounded-lg border transition-all text-left ${
                selectedCard === card.type
                  ? `${colorClass} ring-2 ring-offset-2 ring-offset-zinc-900`
                  : card.available
                  ? "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
                  : "bg-zinc-800/20 border-zinc-800 opacity-50 cursor-not-allowed"
              }`}
            >
              <Icon className={`w-5 h-5 mb-2 ${selectedCard === card.type ? "" : "text-zinc-400"}`} />
              <p className="text-sm font-bold text-white">{card.title}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{card.description}</p>
            </button>
          );
        })}
      </div>

      {/* Generated Card Preview */}
      {generatedSvg && (
        <Card className="bg-zinc-900/80 border-zinc-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
              <Image className="w-4 h-4 text-orange-500" />
              Share Card Preview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* SVG Preview */}
            <div
              className="flex justify-center bg-zinc-950 rounded-lg p-4 border border-zinc-800"
              dangerouslySetInnerHTML={{ __html: generatedSvg }}
            />

            {/* Share Text */}
            {shareText && (
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-xs text-zinc-400 mb-1 font-bold uppercase">Share Caption</p>
                <p className="text-sm text-zinc-300">{shareText}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                onClick={shareNative}
                className="flex-1 bg-orange-600 hover:bg-orange-500 font-bold"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button
                onClick={downloadSvg}
                variant="outline"
                className="border-zinc-600"
                data-testid="download-share-btn"
              >
                <Download className="w-4 h-4" />
              </Button>
              <Button
                onClick={copyShareText}
                variant="outline"
                className="border-zinc-600"
                data-testid="copy-text-btn"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SocialShare;
